import React, { useMemo, useState, useRef, useCallback } from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell
} from 'recharts';

const GammaExposureChart = ({ data, spotPrice, gexLevels, showGexLevels = false, fixedMaxAbsGex = null, onPrevSnapshot, onNextSnapshot, snapshotLabel = null }) => {
  const chartWrapperRef = useRef(null);

  // All strikes sorted (descending - higher strikes at top)
  const allStrikes = useMemo(() => {
    if (!data) return [];
    return data
      .map(item => ({
        strike: item.strike,
        gex: item.totalGex / 1e6,
        callGex: item.callGex / 1e6,
        putGex: item.putGex / 1e6,
        isPositive: item.totalGex >= 0
      }))
      .sort((a, b) => a.strike - b.strike); // Ascending order (lower strikes first in array)
  }, [data]);

  // Find the index of the strike closest to spot price
  const spotIndex = useMemo(() => {
    if (!allStrikes.length || !spotPrice) return 0;
    let closestIdx = 0;
    let closestDiff = Math.abs(allStrikes[0].strike - spotPrice);
    allStrikes.forEach((item, idx) => {
      const diff = Math.abs(item.strike - spotPrice);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIdx = idx;
      }
    });
    return closestIdx;
  }, [allStrikes, spotPrice]);

  // Scroll offset (how many strikes to shift from spot-centered view)
  const [scrollOffset, setScrollOffset] = useState(0);

  // Reset scroll offset only when spotIndex jumps significantly (>2 strikes)
  const prevSpotIndexRef = useRef(spotIndex);
  React.useEffect(() => {
    const diff = Math.abs(spotIndex - prevSpotIndexRef.current);
    prevSpotIndexRef.current = spotIndex;
    if (diff > 2) {
      setScrollOffset(0);
    }
  }, [spotIndex]);

  // Handle wheel scroll on the chart
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -1 : 1; // Scroll down = move to higher strikes
    setScrollOffset(prev => {
      const newOffset = prev + delta;
      // Calculate bounds to prevent scrolling past data
      const maxUp = spotIndex - 20; // Can scroll up until spot is at bottom
      const maxDown = allStrikes.length - spotIndex - 21; // Can scroll down until spot is at top
      return Math.max(-maxUp, Math.min(maxDown, newOffset));
    });
  }, [spotIndex, allStrikes.length]);

  // Add wheel event listener
  React.useEffect(() => {
    const wrapper = chartWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('wheel', handleWheel, { passive: false });
      return () => wrapper.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Get the visible slice of data (21 strikes centered around spot + offset)
  const chartData = useMemo(() => {
    if (!allStrikes.length) return [];

    const centerIdx = spotIndex + scrollOffset;
    const startIdx = Math.max(0, centerIdx - 20);
    const endIdx = Math.min(allStrikes.length, centerIdx + 21);

    return allStrikes.slice(startIdx, endIdx);
  }, [allStrikes, spotIndex, scrollOffset]);

  // Calculate the max absolute value for symmetric axis (use all strikes so bars don't resize on scroll)
  // When fixedMaxAbsGex is provided (snapshot browsing), use it to keep the scale stable
  const maxAbsGex = useMemo(() => {
    if (fixedMaxAbsGex != null) return fixedMaxAbsGex;
    if (!allStrikes.length) return 1;
    return Math.max(...allStrikes.map(d => Math.abs(d.gex))) * 1.1;
  }, [allStrikes, fixedMaxAbsGex]);

  // Build a map of strikes to GEX level names for tooltip display
  const gexLevelsByStrike = useMemo(() => {
    if (!gexLevels) return {};
    const levels = {};

    if (gexLevels.callResistance) {
      levels[gexLevels.callResistance] = levels[gexLevels.callResistance] || [];
      levels[gexLevels.callResistance].push({ name: 'Call Resistance', color: '#ff6b6b' });
    }
    if (gexLevels.putSupport) {
      levels[gexLevels.putSupport] = levels[gexLevels.putSupport] || [];
      levels[gexLevels.putSupport].push({ name: 'Put Support', color: '#51cf66' });
    }
    if (gexLevels.hvl) {
      levels[gexLevels.hvl] = levels[gexLevels.hvl] || [];
      levels[gexLevels.hvl].push({ name: 'HVL', color: '#be4bdb' });
    }
    if (gexLevels.gammaWall0DTE) {
      levels[gexLevels.gammaWall0DTE] = levels[gexLevels.gammaWall0DTE] || [];
      levels[gexLevels.gammaWall0DTE].push({ name: 'Gamma Wall 0DTE', color: '#ffd43b' });
    }
    if (gexLevels.callResistance0DTE) {
      levels[gexLevels.callResistance0DTE] = levels[gexLevels.callResistance0DTE] || [];
      levels[gexLevels.callResistance0DTE].push({ name: 'Call Res 0DTE', color: '#ff6b6b' });
    }
    if (gexLevels.putSupport0DTE) {
      levels[gexLevels.putSupport0DTE] = levels[gexLevels.putSupport0DTE] || [];
      levels[gexLevels.putSupport0DTE].push({ name: 'Put Sup 0DTE', color: '#51cf66' });
    }
    if (gexLevels.hvl0DTE) {
      levels[gexLevels.hvl0DTE] = levels[gexLevels.hvl0DTE] || [];
      levels[gexLevels.hvl0DTE].push({ name: 'HVL 0DTE', color: '#be4bdb' });
    }
    // For 1D range, find nearest strikes
    if (gexLevels.expected1DMax) {
      const nearestMax = chartData.find(d => Math.abs(d.strike - gexLevels.expected1DMax) <= 5);
      if (nearestMax) {
        levels[nearestMax.strike] = levels[nearestMax.strike] || [];
        levels[nearestMax.strike].push({ name: `1D Max (${gexLevels.expected1DMax.toFixed(0)})`, color: '#74c0fc' });
      }
    }
    if (gexLevels.expected1DMin) {
      const nearestMin = chartData.find(d => Math.abs(d.strike - gexLevels.expected1DMin) <= 5);
      if (nearestMin) {
        levels[nearestMin.strike] = levels[nearestMin.strike] || [];
        levels[nearestMin.strike].push({ name: `1D Min (${gexLevels.expected1DMin.toFixed(0)})`, color: '#74c0fc' });
      }
    }

    // Add top GEX strikes to levels
    if (gexLevels.topGexStrikes) {
      gexLevels.topGexStrikes.forEach((s, i) => {
        levels[s.strike] = levels[s.strike] || [];
        const color = s.totalGex >= 0 ? '#00ff88' : '#ff4444';
        levels[s.strike].push({ name: `GEX ${s.rank}`, color, isTopGex: true, rank: s.rank });
      });
    }

    return levels;
  }, [gexLevels, chartData]);

  // Build combined reference lines with merged labels (priority order: HVL > Call/Put Wall > GW > 1D > GEX)
  const combinedGexLevels = useMemo(() => {
    if (!gexLevels || !chartData.length) return [];

    const levelsByStrike = {};

    // Helper to add level to a strike (with priority - lower number = higher priority)
    const addLevel = (strike, label, color, strokeColor, strokeWidth, strokeDasharray, priority) => {
      if (!strike || !chartData.some(d => d.strike === strike)) return;
      if (!levelsByStrike[strike]) {
        levelsByStrike[strike] = { labels: [], strokeColor, strokeWidth, strokeDasharray, topPriority: priority };
      }
      levelsByStrike[strike].labels.push({ label, color, priority });
      // Use the style from highest priority level
      if (priority < levelsByStrike[strike].topPriority) {
        levelsByStrike[strike].strokeColor = strokeColor;
        levelsByStrike[strike].strokeWidth = strokeWidth;
        levelsByStrike[strike].strokeDasharray = strokeDasharray;
        levelsByStrike[strike].topPriority = priority;
      }
    };

    // Add levels in priority order (1 = highest)
    if (gexLevels.hvl) {
      addLevel(gexLevels.hvl, 'HVL', '#be4bdb', '#be4bdb', 2, '3 3', 1);
    }
    if (gexLevels.callResistance) {
      addLevel(gexLevels.callResistance, 'Call Wall', '#ff6b6b', '#ff6b6b', 2, null, 2);
    }
    if (gexLevels.putSupport) {
      addLevel(gexLevels.putSupport, 'Put Wall', '#51cf66', '#51cf66', 2, null, 2);
    }
    if (gexLevels.gammaWall0DTE) {
      addLevel(gexLevels.gammaWall0DTE, 'GW 0DTE', '#ffd43b', '#ffd43b', 2, '2 2', 3);
    }
    if (gexLevels.expected1DMax) {
      const nearestMax = chartData.find(d => Math.abs(d.strike - gexLevels.expected1DMax) ===
        Math.min(...chartData.map(c => Math.abs(c.strike - gexLevels.expected1DMax))));
      if (nearestMax) {
        addLevel(nearestMax.strike, '1D Max', '#74c0fc', '#74c0fc', 1, '4 4', 4);
      }
    }
    if (gexLevels.expected1DMin) {
      const nearestMin = chartData.find(d => Math.abs(d.strike - gexLevels.expected1DMin) ===
        Math.min(...chartData.map(c => Math.abs(c.strike - gexLevels.expected1DMin))));
      if (nearestMin) {
        addLevel(nearestMin.strike, '1D Min', '#74c0fc', '#74c0fc', 1, '4 4', 4);
      }
    }
    if (gexLevels.topGexStrikes) {
      gexLevels.topGexStrikes.forEach((s) => {
        const color = s.totalGex >= 0 ? '#00ff88' : '#ff4444';
        const bgColor = s.totalGex >= 0 ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)';
        addLevel(s.strike, `GEX ${s.rank}`, color, bgColor, 1, null, 5 + s.rank);
      });
    }

    // Convert to array and sort labels by priority within each strike
    return Object.entries(levelsByStrike).map(([strike, data]) => {
      const sortedLabels = data.labels.sort((a, b) => a.priority - b.priority);
      const combinedLabel = sortedLabels.map(l => l.label).join(' / ');
      const primaryColor = sortedLabels[0].color;
      return {
        strike: Number(strike),
        label: combinedLabel,
        labelColor: primaryColor,
        strokeColor: data.strokeColor,
        strokeWidth: data.strokeWidth,
        strokeDasharray: data.strokeDasharray
      };
    });
  }, [gexLevels, chartData]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const levelsAtStrike = gexLevelsByStrike[data.strike];

    return (
      <div style={styles.tooltip}>
        <div style={styles.tooltipTitle}>Strike: {data.strike}</div>
        {levelsAtStrike && levelsAtStrike.length > 0 && (
          <div style={styles.tooltipLevels}>
            {levelsAtStrike.map((level, i) => (
              <span key={i} style={{ color: level.color, fontWeight: 600 }}>
                {level.name}{i < levelsAtStrike.length - 1 ? ' | ' : ''}
              </span>
            ))}
          </div>
        )}
        <div style={{ color: data.gex >= 0 ? '#00ff88' : '#ff4444' }}>
          Total GEX: {data.gex >= 0 ? '+' : ''}{data.gex.toFixed(2)}M
        </div>
        <div style={{ color: '#00ff88' }}>Call GEX: +{data.callGex.toFixed(2)}M</div>
        <div style={{ color: '#ff4444' }}>Put GEX: {data.putGex.toFixed(2)}M</div>
      </div>
    );
  };

  if (!chartData.length) {
    return (
      <div style={styles.noData}>
        No gamma exposure data available
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.chartTitle}>
        Gamma Exposure by Strike
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {snapshotLabel && (
            <span style={styles.snapshotLabel}>{snapshotLabel}</span>
          )}
          <button
            onClick={onPrevSnapshot}
            style={styles.scrollArrow}
            title="Previous snapshot"
          >&#9664;</button>
          <button
            onClick={onNextSnapshot}
            style={styles.scrollArrow}
            title="Next snapshot"
          >&#9654;</button>
        </span>
      </h2>
      <div style={styles.legend}>
        <span style={{ ...styles.legendItem, color: '#00ff88' }}>Positive GEX (Dealer Short Gamma)</span>
        <span style={{ ...styles.legendItem, color: '#ff4444' }}>Negative GEX (Dealer Long Gamma)</span>
      </div>
      <div
        ref={chartWrapperRef}
        style={styles.chartWrapper}
        title="Scroll to move through strikes"
      >
      <ResponsiveContainer width="100%" height={600}>
        <ComposedChart
          layout="vertical"
          data={chartData}
          margin={{ top: 20, right: 30, left: 60, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#30363d" horizontal={true} vertical={true} />
          <XAxis
            type="number"
            domain={[-maxAbsGex, maxAbsGex]}
            allowDataOverflow={!!fixedMaxAbsGex}
            tick={{ fill: '#8b949e', fontSize: 12 }}
            tickFormatter={(value) => `${value >= 0 ? '+' : ''}${value.toFixed(0)}M`}
            axisLine={{ stroke: '#30363d' }}
          />
          <YAxis
            type="category"
            dataKey="strike"
            orientation="left"
            tick={{ fill: '#8b949e', fontSize: 11 }}
            width={55}
            axisLine={{ stroke: '#30363d' }}
            tickFormatter={(value) => value.toLocaleString()}
            reversed={true}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#58a6ff" strokeWidth={1} />
          {/* Spot Price Line */}
          {spotPrice && (
            <ReferenceLine
              y={chartData.find(d => Math.abs(d.strike - spotPrice) ===
                Math.min(...chartData.map(c => Math.abs(c.strike - spotPrice))))?.strike}
              stroke="#ffcc00"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: 'spot',
                fill: '#ffcc00',
                fontSize: 10,
                position: 'right'
              }}
            />
          )}

          {/* GEX Levels Reference Lines - combined labels for overlapping levels */}
          {showGexLevels && combinedGexLevels.map((level, i) => (
            <ReferenceLine
              key={`gex-combined-${i}`}
              y={level.strike}
              stroke={level.strokeColor}
              strokeWidth={level.strokeWidth}
              strokeDasharray={level.strokeDasharray}
              label={{
                value: level.label,
                fill: level.labelColor,
                fontSize: 10,
                position: 'insideBottomRight'
              }}
            />
          ))}

          <Bar dataKey="gex" name="GEX" barSize={8} isAnimationActive={false}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPositive ? '#00ff88' : '#ff4444'}
                fillOpacity={0.8}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      </div>
      <div style={styles.footer}>
        <p style={styles.footerText}>
          GEX values in millions. Positive GEX = dealers are short gamma (price stabilizing).
          Negative GEX = dealers are long gamma (price amplifying).
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d',
    padding: '24px'
  },
  chartTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  scrollArrow: {
    background: 'none',
    border: '1px solid #30363d',
    borderRadius: '4px',
    color: '#8b949e',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '2px 6px',
    lineHeight: 1
  },
  snapshotLabel: {
    fontSize: '12px',
    color: '#58a6ff',
    fontWeight: 600,
    marginRight: '4px'
  },
  chartWrapper: {
    position: 'relative',
    cursor: 'grab',
    userSelect: 'none'
  },
  legend: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    fontSize: '12px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tooltip: {
    backgroundColor: '#1c2128',
    border: '1px solid #30363d',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    lineHeight: 1.6
  },
  tooltipTitle: {
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '4px'
  },
  tooltipLevels: {
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '1px solid #30363d',
    fontSize: '12px'
  },
  footer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #30363d'
  },
  footerText: {
    margin: 0,
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: 1.5
  },
  noData: {
    textAlign: 'center',
    padding: '48px',
    color: '#8b949e',
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d'
  }
};

export default GammaExposureChart;
